import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dv2ElearningImage from './Dv2ElearningImage';
import { resolveElearningImagePair } from './programConfig';
import {
    buildReadBlocks,
    paginateReadBlocks,
    paginateReadSlicesMobile,
} from './readPagination';
import './elearningImageV2.css';
import './ReadLessonReader.css';

/**
 * Internal paginated reader for the Read step only.
 * Content comes from API introText / contentText / imageUrl.
 * V2 may override the in-lesson image by module title (covers), with API fallback.
 */
const ReadLessonReader = ({
    introText,
    contentText,
    imageUrl,
    moduleTitle,
    onContinueToDo,
}) => {
    const rootRef = useRef(null);
    const swipeRef = useRef({ x: null, y: null });
    const swipeElRef = useRef(null);
    const pageIndexRef = useRef(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [fadeKey, setFadeKey] = useState(0);
    const [isMobile, setIsMobile] = useState(
        () =>
            typeof window !== 'undefined' && window.innerWidth <= 767
    );

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const imagePair = useMemo(
        () =>
            resolveElearningImagePair({
                title: moduleTitle,
                legacyPath: imageUrl,
                kind: 'module',
            }),
        [moduleTitle, imageUrl]
    );

    const pages = useMemo(() => {
        const blocks = buildReadBlocks({
            introText,
            contentText,
            // Keep an image block whenever API or V2 has art
            imageUrl: imagePair.src || imagePair.fallbackSrc || '',
        });
        return isMobile
            ? paginateReadSlicesMobile(blocks)
            : paginateReadBlocks(blocks);
    }, [introText, contentText, imagePair.src, imagePair.fallbackSrc, isMobile]);

    const totalPages = pages.length || 1;
    const safeIndex = Math.min(pageIndex, totalPages - 1);
    const pageBlocks = pages[safeIndex] || [];
    const isFirst = safeIndex === 0;
    const isLast = safeIndex >= totalPages - 1;
    const progressPct = Math.round(((safeIndex + 1) / totalPages) * 100);
    const canSwipe = isMobile && totalPages > 1;

    pageIndexRef.current = safeIndex;

    useEffect(() => {
        setPageIndex(0);
        setFadeKey((k) => k + 1);
    }, [introText, contentText, imageUrl, moduleTitle, isMobile]);

    const scrollReaderIntoView = useCallback(() => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const el = rootRef.current;
                if (!el) return;

                let parent = el.parentElement;
                while (parent && parent !== document.body) {
                    const style = window.getComputedStyle(parent);
                    const overflowY = style.overflowY;
                    const canScroll =
                        (overflowY === 'auto' ||
                            overflowY === 'scroll' ||
                            overflowY === 'overlay') &&
                        parent.scrollHeight > parent.clientHeight + 4;
                    if (canScroll) {
                        const parentRect = parent.getBoundingClientRect();
                        const elRect = el.getBoundingClientRect();
                        const delta =
                            elRect.top - parentRect.top + parent.scrollTop - 16;
                        parent.scrollTo({
                            top: Math.max(0, delta),
                            behavior: 'smooth',
                        });
                        return;
                    }
                    parent = parent.parentElement;
                }

                const top =
                    el.getBoundingClientRect().top + window.scrollY - 20;
                window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
            });
        });
    }, []);

    const goToPage = useCallback(
        (next) => {
            if (next < 0 || next >= totalPages) return;
            setPageIndex(next);
            setFadeKey((k) => k + 1);
            scrollReaderIntoView();
        },
        [totalPages, scrollReaderIntoView]
    );

    useEffect(() => {
        const el = swipeElRef.current;
        if (!el || !canSwipe) return undefined;

        const onStart = (event) => {
            const touch = event.changedTouches?.[0];
            if (!touch) return;
            const onControl = Boolean(
                event.target?.closest?.(
                    'button, a, input, textarea, select, .dv2-read__nav-btn'
                )
            );
            swipeRef.current = {
                x: onControl ? null : touch.clientX,
                y: onControl ? null : touch.clientY,
            };
        };

        const onMove = (event) => {
            const start = swipeRef.current;
            const touch = event.touches?.[0];
            if (start.x == null || start.y == null || !touch) return;
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
                event.preventDefault();
            }
        };

        const onEnd = (event) => {
            const start = swipeRef.current;
            swipeRef.current = { x: null, y: null };
            const touch = event.changedTouches?.[0];
            if (start.x == null || start.y == null || !touch) return;
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) < 40) return;
            if (Math.abs(dx) < Math.abs(dy) * 1.05) return;
            if (document.activeElement?.blur) {
                document.activeElement.blur();
            }
            const current = pageIndexRef.current;
            if (dx < 0 && current < totalPages - 1) {
                goToPage(current + 1);
            } else if (dx > 0 && current > 0) {
                goToPage(current - 1);
            }
        };

        el.addEventListener('touchstart', onStart, { passive: true });
        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', onEnd, { passive: true });
        return () => {
            el.removeEventListener('touchstart', onStart);
            el.removeEventListener('touchmove', onMove);
            el.removeEventListener('touchend', onEnd);
        };
    }, [canSwipe, totalPages, goToPage]);

    return (
        <div
            className={`dv2-read${isMobile ? ' dv2-read--mobile' : ''}`}
            ref={rootRef}
        >
            {isMobile ? (
                <p className="dv2-read__kicker">Read</p>
            ) : (
                <h5 className="dv2-read__about-label">About this lesson</h5>
            )}

            <div
                className="dv2-read__progress"
                aria-label={
                    isMobile
                        ? `Read section ${safeIndex + 1} of ${totalPages}`
                        : `Lesson page ${safeIndex + 1} of ${totalPages}`
                }
            >
                <div className="dv2-read__progress-meta">
                    {isMobile ? (
                        <span>
                            Section {safeIndex + 1} of {totalPages}
                        </span>
                    ) : (
                        <>
                            <span>
                                Lesson page {safeIndex + 1} of {totalPages}
                            </span>
                            <span>{progressPct}%</span>
                        </>
                    )}
                </div>
                {isMobile ? (
                    <div
                        className="dv2-read__dots"
                        aria-hidden="true"
                    >
                        {Array.from({ length: totalPages }).map((_, i) => (
                            <span
                                key={`rdot-${i}`}
                                className={`dv2-read__dot${
                                    i === safeIndex ? ' is-on' : ''
                                }${i < safeIndex ? ' is-done' : ''}`}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="dv2-read__progress-bar">
                        <span style={{ width: `${progressPct}%` }} />
                    </div>
                )}
            </div>

            <div
                key={fadeKey}
                ref={swipeElRef}
                className={`dv2-read__page dv2-read__page--fade${
                    canSwipe ? ' is-swipeable' : ''
                }`}
            >
                {pageBlocks.map((block, i) => {
                    if (block.type === 'image') {
                        return (
                            <figure
                                key={`img-${i}`}
                                className={`dv2-read__figure dv2-elimg-v2${
                                    imagePair.isV2
                                        ? ' dv2-read__figure--v2'
                                        : ''
                                }`}
                            >
                                <Dv2ElearningImage
                                    src={imagePair.src}
                                    fallbackSrc={imagePair.fallbackSrc}
                                    alt=""
                                    className="dv2-read__image"
                                    loading="eager"
                                />
                            </figure>
                        );
                    }
                    return (
                        <div
                            key={`html-${i}`}
                            className="dv2-read__block"
                            dangerouslySetInnerHTML={{ __html: block.html }}
                        />
                    );
                })}
                {!pageBlocks.length ? (
                    <p className="dv2-read__empty">
                        No reading content for this module.
                    </p>
                ) : null}
            </div>

            <div className="dv2-read__nav">
                <button
                    type="button"
                    className="dv2-read__nav-btn"
                    onClick={() => goToPage(safeIndex - 1)}
                    disabled={isFirst}
                    aria-label="Previous lesson page"
                >
                    ← Previous
                </button>
                <span className="dv2-read__nav-count">
                    {isMobile
                        ? `${safeIndex + 1} / ${totalPages}`
                        : `Page ${safeIndex + 1} of ${totalPages}`}
                </span>
                {!isLast ? (
                    <button
                        type="button"
                        className="dv2-read__nav-btn dv2-read__nav-btn--next"
                        onClick={() => goToPage(safeIndex + 1)}
                        aria-label="Next lesson page"
                    >
                        Next →
                    </button>
                ) : (
                    <button
                        type="button"
                        className="dv2-read__nav-btn dv2-read__nav-btn--next"
                        onClick={onContinueToDo}
                        aria-label="Continue to Do"
                    >
                        Continue to Do →
                    </button>
                )}
            </div>
        </div>
    );
};

export default ReadLessonReader;
